import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router';
import HomePage from '../views/HomePage.vue';
import { useAuthStore } from '../stores/auth';
import { applyMeta, SITE_NAME } from '../composables/useMeta';

// `title` → static page <title>/og:title (rendered as "<title> — PixelKey").
// `dynamicMeta` → page sets its own meta from async data, so the router skips
// the default reset to avoid clobbering it.
const routes: RouteRecordRaw[] = [
  { path: '/', name: 'home', component: HomePage },
  { path: '/catalog', name: 'catalog', component: () => import('../views/CatalogPage.vue'), meta: { title: 'Catalog' } },
  { path: '/product/:id', name: 'product', component: () => import('../views/ProductPage.vue'), meta: { dynamicMeta: true } },
  { path: '/articles', name: 'articles', component: () => import('../views/ArticlesPage.vue'), meta: { title: 'Articles' } },
  { path: '/articles/:slug', name: 'article', component: () => import('../views/ArticlePage.vue'), meta: { dynamicMeta: true } },
  { path: '/faq', name: 'faq', component: () => import('../views/FaqPage.vue'), meta: { title: 'FAQ' } },
  { path: '/terms', name: 'terms', component: () => import('../views/TermsPage.vue'), meta: { title: 'Terms & Conditions' } },
  { path: '/cookies', name: 'cookies', component: () => import('../views/CookiesPage.vue'), meta: { title: 'Cookie Policy' } },
  { path: '/cart', name: 'cart', component: () => import('../views/CartPage.vue'), meta: { title: 'Cart' } },
  { path: '/checkout', name: 'checkout', component: () => import('../views/CheckoutPage.vue'), meta: { title: 'Checkout', requiresAuth: true } },
  { path: '/account', name: 'account', component: () => import('../views/DashboardPage.vue'), meta: { title: 'Dashboard', requiresAuth: true } },
  { path: '/account/favorites', name: 'favorites', component: () => import('../views/FavoritesPage.vue'), meta: { title: 'Favorites', requiresAuth: true } },
  { path: '/account/settings', name: 'settings', component: () => import('../views/SettingsPage.vue'), meta: { title: 'Settings', requiresAuth: true } },
  { path: '/account/orders', name: 'orders', component: () => import('../views/OrdersPage.vue'), meta: { title: 'Orders', requiresAuth: true } },
  { path: '/account/billing', name: 'billing', component: () => import('../views/BillingPage.vue'), meta: { title: 'Billing', requiresAuth: true } },
  { path: '/wallet/topup/return', name: 'topup-return', component: () => import('../views/TopupReturnPage.vue'), meta: { title: 'Processing payment', requiresAuth: true } },
  { path: '/checkout/success', name: 'checkout-success', component: () => import('../views/OrderSuccessPage.vue'), meta: { title: 'Payment successful', requiresAuth: true } },

  // Auth scaffolding (carried over from the template).
  { path: '/login', name: 'login', component: () => import('../views/LoginPage.vue'), meta: { title: 'Log In' } },
  { path: '/register', name: 'register', component: () => import('../views/RegisterPage.vue'), meta: { title: 'Sign Up' } },
  { path: '/reset-password', name: 'reset-password', component: () => import('../views/ResetPasswordPage.vue'), meta: { title: 'Reset Password' } },
  { path: '/verify-email', name: 'verify-email', component: () => import('../views/VerifyEmailPage.vue'), meta: { title: 'Verify Email' } },

  // Error pages
  { path: '/403', name: 'forbidden', component: () => import('../views/errors/ForbiddenPage.vue'), meta: { title: 'Access Denied' } },
  { path: '/500', name: 'server-error', component: () => import('../views/errors/ServerErrorPage.vue'), meta: { title: 'Server Error' } },
  { path: '/503', name: 'maintenance', component: () => import('../views/errors/ServiceUnavailablePage.vue'), meta: { title: 'Under Maintenance' } },
  // Catch-all 404 — must stay last.
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('../views/errors/NotFoundPage.vue'), meta: { title: 'Page Not Found' } },
];

export const router = createRouter({
  history: createWebHistory('/'),
  routes,
});

// Per-route head defaults. Pages with `dynamicMeta` manage their own tags.
router.afterEach((to) => {
  if (to.meta.dynamicMeta) return;
  const title = to.meta.title as string | undefined;
  applyMeta({ title: title ? `${title} — ${SITE_NAME}` : undefined });
});

// Gate auth-only routes. Waits for the initial session check.
router.beforeEach(async (to) => {
  if (!to.meta.requiresAuth) return true;
  const auth = useAuthStore();
  await auth.ensureReady();
  if (!auth.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } };
  }
  return true;
});
